import type { Schemas } from '#shopware'
import { mediaIncludes, productMediaIncludes } from './media'
import { calculatedPriceIncludes } from './calculatedPrice'
import { propertyGroupIncludes } from './propertyGroup'
import { productManufacturerIncludes } from './productManufacturer'

export const productIncludes: Schemas['Includes'] = {
    product: [
        'id',
        'active',
        'translated',
        'name',
        'description',
        'productNumber',
        'seoUrls',
        'available',
        'stock',
        'isCloseout',
        'calculatedCheapestPrice',
        'calculatedPrices',
        'calculatedPrice',
        'variantListing',
        'parentId',
        'isNew',
        'ean',
        'manufacturerNumber',
        'weight',
        'height',
        'width',
        'length',
        'calculatedMaxPurchase',
        'purchaseSteps',
        'markAsTopseller',
        'manufacturer',
        'metaTitle',
        'metaDescription',
        'hasGifts',
        'configurator',
        'optionIds',
        'options',
        'media',
        'cover',
        'minPurchase',
        'releaseDate',
        'deliveryTime',
        'customFields.custom_product_sale',
        'customFields.limited_offer',
        'customFields.limited_offer_from',
        'customFields.limited_offer_until',
        'customFields.discount',
        'customFields.discount_from',
        'customFields.discount_until',
        'customFields.is_configurable',
        'customFields.custom_fit_sku',
        'customFields.OITM_ManBatchSerial',
        'customFields.team_clothing',
        'customFields.variant_color',
        'customFields.generic_color_old',
        'customFields.gender',
        'customFields.pre_orderable',
        'customFields.pre_order_delivery_time',
        'customFields.greenfeed_link',
        'customFields.greenfeed_link_text',
        'customFields.greenfeed_link_headline',
        'customFields.greenfeed_link_image',
        'customFields.dropship',
        'customFields.titleist_logoball',
        'customFields.custom_a4g_warranty_label_product_years',
        'customFields.custom_a4g_warranty_label_product_hide',
        'courseframe',
        'a4g_brand',
        'netiEasyCouponProduct',
        'ratingAverage',
        'cmsPage',
        'sponsoredProduct',
        'badges',
        'promotionDetails',
        'customFieldsMedia',
        'canonicalProduct',
    ],
    calculated_cheapest_price: [
        'unitPrice',
        'variantId',
        'listPrice',
        'calculatedTaxes',
        'quantity',
        'regulationPrice',
    ],
    seo_url: ['pathInfo', 'isCanonical', 'seoPathInfo'],
    delivery_time: ['name', 'translated'],
    ...productMediaIncludes,
    ...mediaIncludes,
    ...propertyGroupIncludes,
    ...calculatedPriceIncludes,
    ...productManufacturerIncludes,
}

// Manufacturer as rendered on a product card: only the name is shown.
const productCardManufacturerIncludes: Schemas['Includes'] = {
    product_manufacturer: ['id', 'name', 'translated'],
}

// Lean variant of productIncludes for product-card contexts (listing grid, CMS sliders,
// recommendation widgets). Only the fields ProductListingCard + its sub-components read.
// `translated` stays listed on purpose: Shopware limits it to the base fields also present
// here (name, customFields), so multilanguage resolution is kept while the description/meta
// mirror — the bulk of the payload — is dropped. Full productIncludes stays on the PDP.
export const productCardIncludes: Schemas['Includes'] = {
    product: [
        'id',
        'active',
        'translated',
        'name',
        'seoUrls',
        'parentId',
        'calculatedCheapestPrice',
        'calculatedPrices',
        'calculatedPrice',
        'variantListing',
        'manufacturer',
        'media',
        'configurator',
        'hasGifts',
        'sponsoredProduct',
        'badges',
        'promotionDetails',
        'customFields.custom_product_sale',
        'customFields.limited_offer',
        'customFields.limited_offer_from',
        'customFields.limited_offer_until',
        'customFields.discount',
        'customFields.discount_from',
        'customFields.discount_until',
        'customFields.is_configurable',
        'customFields.custom_fit_sku',
        'customFields.team_clothing',
        'customFields.variant_color',
        'customFields.generic_color_old',
        'customFields.gender',
        'customFields.pre_orderable',
        'customFields.pre_order_delivery_time',
        'customFields.titleist_logoball',
    ],
    calculated_cheapest_price: [
        'unitPrice',
        'variantId',
        'listPrice',
        'calculatedTaxes',
        'quantity',
        'regulationPrice',
    ],
    seo_url: ['pathInfo', 'isCanonical', 'seoPathInfo'],
    ...productMediaIncludes,
    ...mediaIncludes,
    ...propertyGroupIncludes,
    ...calculatedPriceIncludes,
    ...productCardManufacturerIncludes,
    // Card swatches (ProductListingCardColors) only read colorHexCode/media/name — never
    // property_group_option.configuration. Override the shared option include to drop it;
    // the PDP keeps the full field set via productIncludes.
    property_group_option: ['id', 'name', 'translated', 'colorHexCode', 'media'],
}

// Wishlist rows: the card set plus the fields only this row renders (quantity-input bounds,
// options list). `productNumber` is also the discriminator useGtmTracking uses to tell a
// Product from a LineItem, so dropping it would silently misroute add_to_cart events.
export const wishlistIncludes: Schemas['Includes'] = {
    ...productCardIncludes,
    product: [
        ...productCardIncludes.product!,
        'productNumber',
        'available',
        'minPurchase',
        'calculatedMaxPurchase',
        'purchaseSteps',
        'options',
    ],
    // The row styles the "Right/left" group differently, so the option needs its group.
    property_group_option: [...productCardIncludes.property_group_option!, 'group'],
}
